import { type CommonTranslationFunction } from '@autone/translations';
import { capitalizeFirstLetter } from '@autone/utils';
import { format } from 'date-fns';

import {
  type AdvancedFiltersCondition,
  type AdvancedFiltersState,
} from '../../advanced-filters';
import {
  type GeneralFilterGroup,
  type GeneralFilterOption,
  isSizeDimension,
  type SelectedGeneralFilters,
} from '../../general-filters';
import { type FilterChipType } from '../types';

export const convertToGeneralFilterChips = ({
  generalFilterOptions,
  selectedGeneralFilters,
  common,
}: {
  generalFilterOptions: GeneralFilterGroup[];
  selectedGeneralFilters: SelectedGeneralFilters;
  common: CommonTranslationFunction;
}): FilterChipType[] => {
  const generalFilterChips: FilterChipType[] = [];
  outerLoop: for (const [groupId, options] of Object.entries(
    selectedGeneralFilters,
  )) {
    const activeOptionIds = getActiveOptionIds(options);
    const groupName = getGroupOptionName({ groupId, generalFilterOptions });

    for (const { id, options } of generalFilterOptions) {
      // if all the available general filter options are selected
      // we have a chip show ex. "Department chosen all"
      if (id === groupId && options.length === activeOptionIds.length) {
        generalFilterChips.push({
          id: groupId,
          translatedFilterName: groupName,
          translatedCondition: common('chosen'),
          translatedPostfix: capitalizeFirstLetter(common('all')),
        });
        // if all options chosen, we want to skip to the next groupId in outer loop
        // else it will display both the "all" and the "chosen" chips
        continue outerLoop;
      }
    }

    if (activeOptionIds.length === 1) {
      const optionName = getGroupOptionName({
        groupId,
        optionId: activeOptionIds[0],
        generalFilterOptions,
      });
      generalFilterChips.push({
        id: groupId,
        translatedFilterName: groupName,
        translatedCondition: common('chosen'),
        translatedPostfix: isSizeDimension(groupId)
          ? optionName
          : capitalizeFirstLetter(optionName),
      });
    }
    if (activeOptionIds.length > 1) {
      generalFilterChips.push({
        id: groupId,
        translatedFilterName: groupName,
        translatedCondition: common('chosen'),
        translatedPostfix: activeOptionIds.length.toLocaleString(),
      });
    }
  }
  return generalFilterChips;
};

export type FilterChipsSelectedAdvancedFilters = Omit<
  AdvancedFiltersState,
  'conditions'
> & {
  conditions: (Omit<AdvancedFiltersCondition, 'value'> & {
    value: unknown;
  })[];
};

const parseDateString = (str: string) => {
  const date = new Date(str);
  if (!Number.isNaN(date.getTime())) {
    return date;
  }
  return null;
};

const _getTranslatedCondition = (
  common: CommonTranslationFunction,
  conditionId: string,
): string => {
  switch (conditionId) {
    case 'eq':
      return common('advanced-filters.equal-to');
    case 'gt':
      return common('advanced-filters.greater-than');
    case 'lt':
      return common('advanced-filters.lower-than');
    case 'ge':
      return common('advanced-filters.greater-than-equal');
    case 'le':
      return common('advanced-filters.lower-than-equal');
    case 'in':
      return common('advanced-filters.in');
    case 'is':
      return common('advanced-filters.is');
    default:
      return '';
  }
};

export const convertToAdvancedFilterChips = ({
  selectedAdvancedFilters,
  advancedFilterColumnOptions,
  common,
}: {
  selectedAdvancedFilters: FilterChipsSelectedAdvancedFilters;
  advancedFilterColumnOptions: GeneralFilterOption[];
  common: CommonTranslationFunction;
}): FilterChipType[] =>
  selectedAdvancedFilters.conditions.map(
    ({ filterId, filterConditionId, value }) => ({
      id: filterId,
      translatedFilterName:
        advancedFilterColumnOptions.find(({ id }) => id === filterId)
          ?.translatedDisplayName ?? '',
      translatedCondition: _getTranslatedCondition(
        common,
        filterConditionId,
      ).toLowerCase(),
      translatedPostfix: (() => {
        if (typeof value === 'string') {
          // could be a date
          const date = parseDateString(value);
          if (date !== null) {
            return format(date, 'dd-MM-yyyy');
          }
          return value;
        } else if (typeof value === 'number') {
          return value.toLocaleString();
        } else if (typeof value === 'object') {
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          throw new Error('Cannot render object values in filter chips');
        } else if (typeof value === 'boolean') {
          return value ? common('yes') : common('no');
        }

        return '';
      })(),
    }),
  );

const getActiveOptionIds = (options: Record<string, boolean>) => {
  const activeOptionIds: string[] = [];
  Object.entries(options).forEach(([optionIds, isActive]) => {
    if (isActive) {
      activeOptionIds.push(optionIds);
    }
  });
  return activeOptionIds;
};

const getGroupOptionName = ({
  groupId,
  optionId,
  generalFilterOptions,
}: {
  groupId: string;
  optionId?: string;
  generalFilterOptions: GeneralFilterGroup[];
}) => {
  const filterGroup = generalFilterOptions.find(({ id }) => id === groupId);
  if (optionId) {
    return (
      filterGroup?.options.find(({ id }) => id === optionId)
        ?.translatedDisplayName ?? ''
    );
  }
  return filterGroup?.translatedDisplayName ?? '';
};
